/**
 * Civil dates. Every day decision uses ServiceDateKey (yyyyMMdd) in America/New_York; appointments and
 * slots are stored at noon UTC. Never derive a day by converting a stored UTC midnight.
 * No dependencies, so both the app and the API can use it.
 */
export const SERVICE_TZ = 'America/New_York';

const keyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: SERVICE_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The service day (yyyyMMdd, America/New_York) that contains this instant. */
export function serviceDateKey(instant: Date = new Date()): string {
  const parts = keyFmt.formatToParts(instant);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}${get('month')}${get('day')}`;
}

export function isDateKey(s: string): boolean {
  if (!/^\d{8}$/.test(s)) return false;
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(4, 6));
  const d = Number(s.slice(6, 8));
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** yyyyMMdd → yyyy-mm-dd */
export function keyToIsoDay(key: string): string {
  return `${key.slice(0, 4)}-${key.slice(4, 6)}-${key.slice(6, 8)}`;
}

/** The stored instant for a civil day: noon UTC. */
export function keyToNoonUtc(key: string): string {
  return `${keyToIsoDay(key)}T12:00:00Z`;
}

/** Add whole days to a civil day key (calendar arithmetic at noon UTC, never across a DST edge). */
export function addDaysToKey(key: string, days: number): string {
  const d = new Date(keyToNoonUtc(key));
  d.setUTCDate(d.getUTCDate() + days);
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** The civil day of a stored noon-UTC value (read the date part; never convert through a zone). */
export function storedNoonToKey(stored: string): string {
  return stored.slice(0, 10).replace(/-/g, '');
}

/** 0 = Sunday … 6 = Saturday, for a civil day. */
export function dayOfWeek(key: string): number {
  return new Date(keyToNoonUtc(key)).getUTCDay();
}

export function isWeekdayKey(key: string): boolean {
  const d = dayOfWeek(key);
  return d !== 0 && d !== 6;
}

/** Monday of the Mon–Sun week that holds a civil day. */
export function mondayOfKey(key: string): string {
  const d = dayOfWeek(key);
  return addDaysToKey(key, d === 0 ? -6 : 1 - d);
}

/** First day of the month `delta` months from the one that holds `key`. */
export function monthStartKey(key: string, delta = 0): string {
  const y = Number(key.slice(0, 4));
  const m = Number(key.slice(4, 6)) - 1 + delta;
  const d = new Date(Date.UTC(y, m, 1, 12));
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}01`;
}

export function monthEndKey(key: string): string {
  return addDaysToKey(monthStartKey(key, 1), -1);
}

/** Minutes after midnight, now, on the service clock (America/New_York). */
export function serviceMinutesNow(instant: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: 'numeric', hourCycle: 'h23', timeZone: SERVICE_TZ }).formatToParts(instant);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return get('hour') * 60 + get('minute');
}

/**
 * 1.7.8: the instant of a clock time on a civil day, on the service clock ("2:30 PM on 8 Oct" as an instant).
 * Worked from the stored noon of the day, so it never asks what a UTC midnight is.
 */
export function serviceInstant(dayKey: string, minutes: number): Date {
  const noon = new Date(keyToNoonUtc(dayKey));
  return new Date(noon.getTime() + (minutes - serviceMinutesNow(noon)) * 60_000);
}
